/**
 * useStageProgramState — the family adapts the bought stage programme in one sentence.
 *
 * The sheet takes the sentence; « Recomposer » sends it and the programme fades while three steps
 * tick (`<RecomposeSteps>`). The answer is held aside until the last step is ticked, then shown: the
 * chapters it brought read « selon votre demande ». « Revenir au programme proposé » sends an empty
 * sentence, the same round-trip. The answer of a recomposition is the programme in place from then
 * on: it holds over the one first read (`useStageProgramData`).
 *
 * A refusal because the programme moved under the page (409: the stage starts, another adaptation
 * is under way, nothing left to come back from) drops what the page holds and reads it again; the
 * reason stays on the page. A sentence that failed otherwise reopens the sheet on it; a failed
 * « Revenir » has no sheet to reopen, its reason stays on the page.
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useNavigation } from "@react-navigation/native"
import { useCallback, useReducer } from "react"

import { stageProgramService } from "../../../../../../data/services/stageProgramService"
import {
    stageProgramChapterIds,
    type ApiStageProgram,
} from "../../../../adapters/parent/StageProgram/stageProgramAdapter"
import { useApiMutation } from "../../../hooks/useApiMutation"

/** One sentence, the bound the server holds. */
export const STAGE_PROGRAM_INSTRUCTIONS_MAX = 400

export type StageProgramUiState = {
    sheetOpen: boolean
    draft: string
    /** The sentence sent — null for « Revenir au programme proposé ». */
    sent: string | null
    recomposing: boolean
    /** The answer, held until the steps are ticked. */
    pending: ApiStageProgram | null
    /** The programme in place since the last recomposition. */
    applied: ApiStageProgram | null
    newChapterIds: ReadonlySet<number>
    /** What went wrong, in the sheet. */
    errorMessage: string | null
    /** What went wrong, on the page — when there is no sheet to say it. */
    notice: string | null
}

export type StageProgramAction =
    | { type: "OPEN_SHEET"; instructions: string | null }
    | { type: "CLOSE_SHEET" }
    | { type: "SET_DRAFT"; draft: string }
    | { type: "SEND"; sentence: string | null }
    | { type: "ANSWERED"; program: ApiStageProgram }
    | { type: "FAILED"; message: string }
    | { type: "STALE"; message: string }
    | { type: "REVEAL"; before: ApiStageProgram | null }

const NO_CHAPTERS: ReadonlySet<number> = new Set()

export const INITIAL_STAGE_PROGRAM_STATE: StageProgramUiState = {
    sheetOpen: false,
    draft: "",
    sent: null,
    recomposing: false,
    pending: null,
    applied: null,
    newChapterIds: NO_CHAPTERS,
    errorMessage: null,
    notice: null,
}

export function stageProgramReducer(state: StageProgramUiState, action: StageProgramAction): StageProgramUiState {
    switch (action.type) {
        case "OPEN_SHEET":
            // « Modifier » reopens on the sentence in place: nothing to retype.
            return { ...state, sheetOpen: true, draft: action.instructions ?? "", errorMessage: null, notice: null }
        case "CLOSE_SHEET":
            return { ...state, sheetOpen: false }
        case "SET_DRAFT":
            return { ...state, draft: action.draft.slice(0, STAGE_PROGRAM_INSTRUCTIONS_MAX) }
        case "SEND":
            return {
                ...state,
                sheetOpen: false,
                sent: action.sentence,
                recomposing: true,
                pending: null,
                errorMessage: null,
                notice: null,
            }
        case "ANSWERED":
            return { ...state, pending: action.program }
        case "FAILED": {
            // A sentence: the sheet comes back on it, with what went wrong — nothing to retype.
            const fromSheet = state.sent !== null
            return {
                ...state,
                sheetOpen: fromSheet,
                recomposing: false,
                pending: null,
                errorMessage: fromSheet ? action.message : null,
                notice: fromSheet ? null : action.message,
            }
        }
        case "STALE":
            // The programme moved under the page: what it held goes, the read comes again.
            return {
                ...state,
                sheetOpen: false,
                recomposing: false,
                pending: null,
                applied: null,
                newChapterIds: NO_CHAPTERS,
                errorMessage: null,
                notice: action.message,
            }
        case "REVEAL": {
            if (!state.pending) return state
            // What lights up: the chapters that were not on screen just before. Coming back to the
            // programme proposed lights nothing.
            const shown = new Set(stageProgramChapterIds(action.before))
            const newChapterIds =
                state.sent !== null
                    ? new Set(stageProgramChapterIds(state.pending).filter((id) => !shown.has(id)))
                    : NO_CHAPTERS
            return { ...state, recomposing: false, applied: state.pending, pending: null, newChapterIds }
        }
    }
}

/** The server's « the programme moved under you » — the window closed, a concurrent adaptation, nothing to restore. */
const isStale = (error: unknown) => (error as { status?: number } | null)?.status === 409

export function useStageProgramState(read: ApiStageProgram | null, reread: () => void) {
    const [ui, dispatchUi] = useReducer(stageProgramReducer, INITIAL_STAGE_PROGRAM_STATE)
    const navigation = useNavigation<{
        canGoBack: () => boolean
        goBack: () => void
        replace: (name: string) => void
    }>()
    const program = ui.applied ?? read

    const { start } = useApiMutation({
        service: stageProgramService.adaptStageProgram,
        fallback: "Le programme n'a pas pu être recomposé. Réessayez dans un instant.",
        skipWhilePending: true,
        onSuccess: (data) => dispatchUi({ type: "ANSWERED", program: data as ApiStageProgram }),
        onError: (message, error) => {
            if (!isStale(error)) return dispatchUi({ type: "FAILED", message })
            dispatchUi({ type: "STALE", message })
            reread()
        },
    })

    const send = useCallback(
        (sentence: string | null) => {
            if (!start({}, { instructions: sentence ?? "" })) return
            dispatchUi({ type: "SEND", sentence })
            amplitude.track(sentence !== null ? "parent_stage_program.adapted" : "parent_stage_program.reset")
        },
        [start]
    )

    const actions = {
        close() {
            if (ui.recomposing) return
            if (navigation.canGoBack()) navigation.goBack()
            else navigation.replace("menu")
        },
        openSheet: () => dispatchUi({ type: "OPEN_SHEET", instructions: program?.instructions ?? null }),
        closeSheet: () => dispatchUi({ type: "CLOSE_SHEET" }),
        setDraft: (draft: string) => dispatchUi({ type: "SET_DRAFT", draft }),
        // The start of a sentence keeps its trailing space: the family types right after it.
        addExample: (text: string) =>
            dispatchUi({ type: "SET_DRAFT", draft: ui.draft.trim() ? `${ui.draft.trimEnd()} ${text}` : text }),
        recompose: () => {
            if (ui.draft.trim() !== "") send(ui.draft.trim())
        },
        reset: () => send(null),
        reveal: () => dispatchUi({ type: "REVEAL", before: program }),
    }

    // A sentence is written, and it is not the one in place.
    const canRecompose = ui.draft.trim() !== "" && ui.draft.trim() !== (program?.instructions ?? "")

    return { ui, program, canRecompose, actions }
}
